import * as Phaser from 'phaser';
import { PALETTE } from './palette';

const STAGE_W = 960;
const STAGE_H = 720;
const STAR_COUNT = 90;
const ATMO_DEPTH = -1000;

interface Star {
  x: number;
  y: number;
  size: number;
  baseAlpha: number;
  peakAlpha: number;
  twinkle: boolean;
  delayMs: number;
}

const STAR_SEED: ReadonlyArray<Star> = (() => {
  // deterministic pseudo-random so the star field is stable across reloads.
  let s = 1337;
  const rnd = (): number => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  const out: Star[] = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    const tier = rnd();
    const bright = tier < 0.15;
    const baseAlpha = bright ? 0.55 : tier < 0.55 ? 0.32 : 0.18;
    const peakAlpha = bright ? 0.95 : tier < 0.55 ? 0.6 : 0.32;
    out.push({
      x: rnd() * STAGE_W,
      y: rnd() * STAGE_H,
      size: bright ? 1.6 : 1.0,
      baseAlpha,
      peakAlpha,
      twinkle: rnd() < 0.4,
      delayMs: Math.round(rnd() * 4000),
    });
  }
  return out;
})();

/**
 * Build the nocturnal-observatory background — faint nebular glows, a star
 * field, and a soft vignette. All layers are non-interactive and sit below
 * gameplay (negative depth).
 *
 * Pass `intensity` < 1 to dim the nebula on screens where it would compete
 * with foreground detail (puzzles, how-to-play).
 */
export function addAtmosphere(scene: Phaser.Scene, intensity = 1): void {
  // 1. Base wash so the canvas reads as deeper than the grid cells.
  const bg = scene.add
    .rectangle(STAGE_W / 2, STAGE_H / 2, STAGE_W, STAGE_H, PALETTE.BG)
    .setDepth(ATMO_DEPTH)
    .setOrigin(0.5);
  bg.setBlendMode(Phaser.BlendModes.NORMAL);

  // 2. Three soft nebula blobs at fixed positions, drawn as graduated ellipses.
  const nebula = scene.add.graphics().setDepth(ATMO_DEPTH + 1);
  nebula.setAlpha(intensity);
  paintNebula(nebula, 220, 200, 360, 280, PALETTE.NEBULA_BLUE, 0.12);
  paintNebula(nebula, 760, 130, 280, 200, PALETTE.NEBULA_PURPLE, 0.08);
  paintNebula(nebula, 580, 560, 380, 280, PALETTE.NEBULA_TEAL, 0.07);

  // 3. Star field. One Graphics holds all dots; bright ones get tweened.
  const stars = scene.add.graphics().setDepth(ATMO_DEPTH + 2);
  for (const star of STAR_SEED) {
    stars.fillStyle(star.size > 1.2 ? PALETTE.STAR_BRIGHT : PALETTE.STAR, star.baseAlpha);
    stars.fillCircle(star.x, star.y, star.size);
  }
  // Animate a handful of bright twinklers via additive overlay sprites.
  const twinkleGfx = scene.add.graphics().setDepth(ATMO_DEPTH + 3);
  for (const star of STAR_SEED) {
    if (!star.twinkle || star.size < 1.2) continue;
    const dot = scene.add
      .circle(star.x, star.y, star.size * 1.4, 0xffffff, 0)
      .setDepth(ATMO_DEPTH + 4)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: dot,
      alpha: { from: star.baseAlpha * 0.4, to: star.peakAlpha * 0.7 },
      duration: 1700,
      delay: star.delayMs,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
  twinkleGfx.clear(); // present for symmetry; per-star objects do the work.

  // 4. Vignette — darkens corners so the action centres visually.
  const vignette = scene.add.graphics().setDepth(ATMO_DEPTH + 5);
  paintVignette(vignette);
}

function paintNebula(
  gfx: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  color: number,
  peakAlpha: number,
): void {
  // Approximate a radial glow as 6 concentric ellipses with diminishing alpha.
  const rings = 6;
  for (let i = rings - 1; i >= 0; i--) {
    const t = (i + 1) / rings;
    const a = peakAlpha * (1 - t) * 0.9;
    if (a <= 0.005) continue;
    gfx.fillStyle(color, a);
    gfx.fillEllipse(cx, cy, rx * t, ry * t);
  }
}

function paintVignette(gfx: Phaser.GameObjects.Graphics): void {
  // Four corner shadows; the lower row is slightly heavier so the level title
  // (top-left) doesn't compete with chrome below.
  const corners: Array<[number, number, number]> = [
    [0, 0, 0.42],
    [STAGE_W, 0, 0.4],
    [0, STAGE_H, 0.55],
    [STAGE_W, STAGE_H, 0.55],
  ];
  for (const [cx, cy, peak] of corners) {
    const rings = 8;
    for (let i = 0; i < rings; i++) {
      const t = i / (rings - 1);
      const r = Phaser.Math.Linear(160, 720, t);
      const a = peak * (1 - t) * 0.18;
      if (a <= 0.005) continue;
      gfx.fillStyle(0x000000, a);
      gfx.fillCircle(cx, cy, r);
    }
  }
}
